import React, { useRef, useEffect } from 'react';
import { MessageBanner } from '@/components';
import {
  BodyLargeEmphasized,
  BodyMedium,
  ButtonPrimaryMedium,
  ButtonFlatMedium,
} from '@/design-system';
import { Add as AddIcon } from '@mui/icons-material';
import accountIcon from '@/assets/account-icon.svg';
import infoIcon from '@/assets/info-icon.svg';
import accountGreenIcon from '@/assets/account-green-icon.svg';
import accountInfoIcon from '@/assets/account-icon-pencil.svg';
import ChildrenEmptyStateIcon from '@/assets/children-empty-state-icon.svg';
import { ChildNewModel } from '@/hooks/useProfile';
import colors from '@/theme/colors';
import ChildEnrollment from '../components/ChildEnrollment/ChildEnrollment';
import {
  ChildGroupByCommunity,
  InstitutionFromSearch,
  GradeChangeInput,
  PendingSchools,
} from '../AccountInformation.types';
import { bannersMessages, grades as gradeOptions } from '../AccountInformation.constants';
import styles from '../AccountInformation.styles';

interface SchoolInformationProps {
  userId: number | null;
  managedBy: string;
  childrenGrouped: ChildGroupByCommunity[];
  pendingSchools: PendingSchools[];
  childrenList: ChildNewModel[] | null;
  childrenAddingIds: Array<string>;
  accountManagementEnabled: boolean;
  revertBlocked: boolean;
  changeManagedByStatus: () => void;
  lookUpSchools: (options, callback) => void;
  handleSubmit: (childUId: string) => Promise<void> | void;
  handleSchoolChange: (
    value: InstitutionFromSearch & { schoolId: number; districtId: number },
    id: number
  ) => void;
  handleGradeChange: (input: GradeChangeInput, childUId: string) => void;
  handleRemoveChild: (childUId: string) => Promise<void> | void;
  handleRemoveAddedChild: (childUId: string) => void;
  handleAddChild: () => void;
  handleCancel: (childUId: string) => void;
  handleDismissPendingNotification: (guardianId: number) => void;
  childUidToFocus: string | null;
  onChildFocused: () => void;
}

export const SchoolInformation: React.FC<SchoolInformationProps> = ({
  userId,
  managedBy,
  childrenGrouped,
  pendingSchools,
  childrenList,
  childrenAddingIds,
  accountManagementEnabled,
  revertBlocked,
  changeManagedByStatus,
  lookUpSchools,
  handleSubmit,
  handleSchoolChange,
  handleGradeChange,
  handleRemoveChild,
  handleRemoveAddedChild,
  handleAddChild,
  handleCancel,
  handleDismissPendingNotification,
  childUidToFocus,
  onChildFocused,
}) => {
  const addChildButtonRef = useRef<HTMLButtonElement>(null);
  const prevChildrenAddingIdsLength = useRef(childrenAddingIds.length);

  useEffect(() => {
    if (childrenAddingIds.length > prevChildrenAddingIdsLength.current) {
      const lastIndex = childrenAddingIds.length - 1;
      const newId = `new-child-${lastIndex}`;
      window.requestAnimationFrame(() => {
        document.getElementById(newId)?.focus();
      });
    }
    prevChildrenAddingIdsLength.current = childrenAddingIds.length;
  }, [childrenAddingIds.length]);

  useEffect(() => {
    if (!childUidToFocus) return;
    window.requestAnimationFrame(() => {
      const fieldset = document.querySelector(`fieldset[data-child-uid="${childUidToFocus}"]`);
      const removeBtn = fieldset?.querySelector<HTMLElement>('[data-testid="removeChildButton"]');
      removeBtn?.focus();
      onChildFocused();
    });
  }, [childUidToFocus]);

  const prevManagedBy = useRef(managedBy);
  useEffect(() => {
    if (managedBy !== prevManagedBy.current) {
      window.requestAnimationFrame(() => {
        const banner = document.querySelector(
          '[data-testid="manageByParentBanner"], [data-testid="manageByDistrictBanner"]'
        );
        if (banner instanceof HTMLElement) {
          banner.tabIndex = -1;
          banner.focus();
        }
      });
    }
    prevManagedBy.current = managedBy;
  }, [managedBy]);

  const focusAddChildButton = (): void => {
    window.requestAnimationFrame(() => {
      addChildButtonRef.current?.focus();
    });
  };

  const InfoIcon = () => <img src={infoIcon} className={styles.paddingInfoIcon} alt="" />;

  const renderPendingSchools = () => (
    <>
      {pendingSchools.map(p => (
        <div className={styles.pendingSchoolsContainer}>
          <span className={styles.pendingDistrictName}>{p.districtName}</span>
          {p.schools.map(({ institutionName, gradeLevel, isNewSchool }) => (
            <div className={styles.pendingInfoContainer}>
              <div className={styles.alignPendingInfo}>
                {isNewSchool && <InfoIcon />}
                <span className={styles.pendingInfo}>{institutionName}</span>
              </div>
              <div className={styles.alignPendingInfo}>
                <InfoIcon />
                <span className={styles.pendingInfo}>
                  Grade {`${gradeLevel === 'N' || !gradeLevel ? 'Unknown' : gradeLevel}`}
                </span>
              </div>
            </div>
          ))}
        </div>
      ))}
    </>
  );

  const renderChildrenGrouped = () =>
    childrenGrouped.map(({ districts }, groupIndex) => (
      <div key={groupIndex}>
        {districts.map(({ district, childrenIds }, districtIndex) => {
          const groupSeq = groupIndex + 1;
          const districtHeadingId = `district-${groupSeq}`;
          const districtDescId = `district-${groupSeq}-desc`;
          const districtReqId = `district-${groupSeq}-req`;
          return (
            <div key={district} className={styles.districtGroup}>
              <div className={styles.districtNameContainer}>
                <div className={styles.districtName}>
                  <BodyLargeEmphasized
                    id={districtHeadingId}
                    data-testid="districtName"
                    nonTranslate
                    as="h3"
                  >
                    {district}
                  </BodyLargeEmphasized>
                </div>
                <div>
                  <BodyMedium id={districtDescId}>
                    Enter the school your child or children attend. Only schools that are using
                    Peachjar are available for selection.
                  </BodyMedium>
                </div>
                <div style={{ marginTop: '8px' }}>
                  <BodyMedium id={districtReqId} color={colors.slate}>
                    <span style={{ color: colors.dragon }}>*</span> Both school and grade are
                    required.
                  </BodyMedium>
                </div>
              </div>
              <div
                role="group"
                aria-labelledby={districtHeadingId}
                aria-describedby={`${districtDescId} ${districtReqId}`}
              >
                {childrenIds.map((childUid, index) => {
                  const child = childrenList!.find(c => c.childUid === childUid);
                  if (!child) return null;

                  const { institutionName, gradeLevel, grades, wasChanged } = child;

                  const currentGrade = (grades || gradeOptions).find(
                    g => g.description === gradeLevel
                  );
                  const pendingDistrict = pendingSchools.find(d => d.districtName === district);
                  const gradeLevelChanged = pendingDistrict
                    ? pendingDistrict.schools.some(
                        s => s.institutionName === institutionName && !s.isNewSchool
                      )
                    : false;

                  return (
                    <ChildEnrollment
                      id={`child-${groupSeq}-${index}`}
                      key={childUid}
                      childUid={childUid}
                      childIndex={index + 1}
                      disabled={!accountManagementEnabled}
                      grades={grades || gradeOptions}
                      wasAdded={false}
                      wasChanged={wasChanged}
                      institution={child}
                      currentGrade={currentGrade}
                      lookUpSchools={lookUpSchools}
                      handleSubmit={async childUId => {
                        await handleSubmit(childUId);
                      }}
                      handleSchoolChange={handleSchoolChange}
                      handleGradeChange={handleGradeChange}
                      handleRemoveChild={async childUId => {
                        await handleRemoveChild(childUId);
                      }}
                      handleCancel={handleCancel}
                      gradeLevelChanged={gradeLevelChanged}
                      focusAddChildButton={focusAddChildButton}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
        {groupIndex < childrenGrouped.length - 1 && <div className={styles.separator} />}
      </div>
    ));

  const renderAddedChildren = () =>
    childrenAddingIds.map((childUid, index) => {
      const child = childrenList!.find(c => c.childUid === childUid)!;
      const { gradeLevel, grades, wasAdded, wasChanged } = child;
      const currentGrade = (grades || gradeOptions).find(g => g.description === gradeLevel);

      return (
        <ChildEnrollment
          id={`new-child-${index}`}
          childUid={childUid}
          childIndex={index + 1}
          disabled={false}
          grades={grades || gradeOptions}
          wasAdded={wasAdded}
          wasChanged={wasChanged}
          institution={child}
          currentGrade={currentGrade}
          lookUpSchools={lookUpSchools}
          handleSubmit={childUId => {
            handleSubmit(childUId);
          }}
          handleSchoolChange={handleSchoolChange}
          handleGradeChange={handleGradeChange}
          handleRemoveChild={childUId => {
            handleRemoveAddedChild(childUId);
            focusAddChildButton();
          }}
          handleCancel={childUId => {
            handleRemoveAddedChild(childUId);
            focusAddChildButton();
          }}
          gradeLevelChanged={false}
          focusAddChildButton={focusAddChildButton}
        />
      );
    });

  const renderButtonAdd = () =>
    accountManagementEnabled ? (
      // TODO
      <ButtonFlatMedium
        ref={addChildButtonRef}
        onClick={handleAddChild}
        data-testid="addChildButton"
        startAdornment={<AddIcon />}
      >
        Add Child
      </ButtonFlatMedium>
    ) : null;

  return (
    <div className={styles.container}>
      <div className={styles.districtGroup}>
        {managedBy === 'sis' && (
          <div className={styles.manageMyselfMessage}>
            <MessageBanner
              subheadline={bannersMessages.manageByDistrict}
              iconSrc={accountIcon}
              variant="prussian"
              dataTestId="manageByDistrictBanner"
              footer={
                <div className={styles.buttonManageMyself}>
                  <ButtonPrimaryMedium
                    onClick={changeManagedByStatus}
                    data-testid="manageMyselfButton"
                    fontColor={colors.jungle}
                    background={colors.jungle100}
                  >
                    Manage Myself
                  </ButtonPrimaryMedium>
                </div>
              }
            >
              <BodyMedium color={colors.white}>
                {bannersMessages.manageByDistrictDisclaimer}
              </BodyMedium>
            </MessageBanner>
          </div>
        )}
        {managedBy === 'family' && (
          <MessageBanner
            subheadline={bannersMessages.manageByParent}
            iconSrc={accountGreenIcon}
            variant="pop"
            dataTestId="manageByParentBanner"
          />
        )}
        {!revertBlocked && managedBy === 'family' && (
          <MessageBanner
            iconSrc={infoIcon}
            subheadline={bannersMessages.revertManageBy}
            variant="warning"
            dataTestId="revertManageByBanner"
            footer={
              <div className={styles.revertToDistrict} data-testid="revertManageByButton">
                <ButtonFlatMedium
                  onClick={changeManagedByStatus}
                  fontColor={colors.shadesJungle800}
                >
                  Revert to district managed
                </ButtonFlatMedium>
              </div>
            }
          />
        )}
        {managedBy === 'family' && (
          <div className={styles.manageByParentDisclaimer}>
            <BodyMedium className={styles.description} data-testid="manageByParentDisclaimer">
              Access flyers from your child's school and school-approved community enrichment
              resources.
            </BodyMedium>
          </div>
        )}
        {pendingSchools && pendingSchools.length > 0 && (
          <div className={styles.childNotUpToDateBanner}>
            <MessageBanner
              subheadline={bannersMessages.childNotbeUpToDate}
              iconSrc={accountInfoIcon}
              dataTestId="childNotBeUpToDateBanner"
              footer={
                <div className={styles.dismissButton}>
                  <ButtonFlatMedium
                    onClick={() => handleDismissPendingNotification(userId!)}
                    data-testid="dismissPendingNotificationButton"
                    fontColor={colors.shadesJungle800}
                  >
                    Dismiss
                  </ButtonFlatMedium>
                </div>
              }
            >
              {renderPendingSchools()}
              <BodyMedium color={colors.prussian}>
                {bannersMessages.childNotbeUptoDateDisclaimer}
              </BodyMedium>
            </MessageBanner>
          </div>
        )}
        {!childrenList ||
          (childrenList.length === 0 && (
            <div className={styles.emptyState} data-testid="emptyState">
              <img src={ChildrenEmptyStateIcon} alt="" />
              <p>{bannersMessages.childrenEmptyState}</p>
              {renderButtonAdd()}
            </div>
          ))}
        {renderChildrenGrouped()}
        {renderAddedChildren()}
      </div>
      {childrenList && childrenList.length > 0 && (
        <div className={styles.addSectionContainer}>{renderButtonAdd()}</div>
      )}
    </div>
  );
};
